import { Injectable, OnDestroy } from '@angular/core'
import {
  SavedViewUpdate,
  WorkspaceService,
  WorkspaceUpdate,
} from './workspace.service'
import { HttpWorkspaceService } from './http/http-workspace-service'
import { LocalWorkspaceService } from './local-storage/local-workspace-service'
import {
  RootState,
  SavedView,
  setUsingLocalStorage,
  Workspace,
  WorkspaceSummary,
} from '@helios/state-management'
import { AgGridState } from '@northfork/ag-grid-angular'
import { Store } from '@ngrx/store'
import { Subject } from 'rxjs'
import { ToastService } from '@northfork/ng-basics'

@Injectable({
  providedIn: 'root',
})
/**
 * The purpose of this class is to make Helios UI still usable when we are having issues with the ui-storage ws.
 * One challenge is knowing when to fail over. At first this will be pretty naive but can easily be fine-tuned within
 * this class.
 *
 * In this class, we selectively make writes to Local Storage when http requests are made. One might wonder why do we
 * write when we get workspaces but not summaries. The reason is that we do not want to write data that, by itself,
 * will not be usable. It is not a good user experience to have the workspace catalog drawer have a list of all of their
 * workspaces, but not enough data to actually load one of these. Since we only write data when we get a usable set of
 * data, like a whole workspace or a whole saved view, this means we are lazily loading Local Storage with whatever
 * the user actually uses in their workday. Currently, updates while on http do not get propagated to local storage -
 * we just wait until the next time the user gets the workspace or saved view to update it - this is more efficient and
 * less complicated. We could write-through updates later.
 */
export class WorkspaceDelegateService
  extends WorkspaceService
  implements OnDestroy
{
  /**
   * @internal
   */
  hasFailedOver = false
  private readonly destroySubject$ = new Subject<void>()

  constructor(
    private readonly httpService: HttpWorkspaceService,
    private readonly localService: LocalWorkspaceService,
    private readonly store: Store<RootState>,
    private readonly toastService: ToastService,
  ) {
    super()
  }

  ngOnDestroy() {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  triggerFailover(reason: string) {
    if (this.hasFailedOver) {
      return
    }

    this.hasFailedOver = true
    this.store.dispatch(setUsingLocalStorage())

    const failoverMessage = `Falling back to local storage for workspaces. ${reason}`
    console.error(failoverMessage) //For Fullstory
    this.toastService.error(failoverMessage)
  }

  async getUserWorkspaceSummaries(): Promise<WorkspaceSummary[]> {
    const workspaceSummaries =
      await this.getDelegatedService().getUserWorkspaceSummaries()

    /**
     * This is the only block that concerns me. I worry that ui storage will be intermittently failing. Say a user has
     * been working in local storage for a while and is on a nice parallel track, then ui-storage works and user does a
     * helios refresh and then all the prior local storage work gets deleted. After that, ui-storage precipitously
     * stops working, which causes them to go back to using local storage and all the prior changes are lost. I think
     * this is an acceptable failure. It would be hard to detect and at least the user gets to use their most recent
     * usable workspace. Not doing this could result in endlessly writing to local storage without cleaning up.
     */
    if (!this.hasFailedOver) {
      try {
        await this.localService.cleanUpWorkspaces(workspaceSummaries)
      } catch (e) {
        console.error(
          `Error syncing workspace summaries to local storage: ${e}}`,
        )
      }
    }

    return workspaceSummaries
  }

  getWorkspaceSummary(
    workspacePrefix: string,
  ): Promise<WorkspaceSummary | undefined> {
    return this.getDelegatedService().getWorkspaceSummary(workspacePrefix)
  }

  async getWorkspace(workspacePrefix: string): Promise<Workspace | undefined> {
    const workspace = await this.getDelegatedService().getWorkspace(
      workspacePrefix,
    )
    if (workspace == null) {
      return undefined
    }

    if (!this.hasFailedOver) {
      try {
        await this.localService.createWorkspace(workspace, true)
      } catch (e) {
        console.error(`Error syncing workspace to local storage: ${e}}`)
      }
    }

    return workspace
  }

  getDefaultWorkspacePrefix(): Promise<string | undefined> {
    return this.getDelegatedService().getDefaultWorkspacePrefix()
  }

  async getDefaultWorkspace(): Promise<Workspace | undefined> {
    const defaultWorkspace =
      await this.getDelegatedService().getDefaultWorkspace()
    if (defaultWorkspace == null) {
      return undefined
    }

    if (!this.hasFailedOver) {
      try {
        await this.localService.createWorkspace(defaultWorkspace, true)
        await this.localService.makeWorkspaceDefault(
          defaultWorkspace.workspacePrefix,
        )
      } catch (e) {
        console.error(`Error syncing default workspace to local storage: ${e}}`)
      }
    }

    return defaultWorkspace
  }

  async createWorkspace(workspace: Workspace): Promise<string> {
    return this.getDelegatedService().createWorkspace(workspace)
  }

  makeWorkspaceDefault(workspacePrefix: string): Promise<void> {
    return this.getDelegatedService().makeWorkspaceDefault(workspacePrefix)
  }

  updateWorkspace(
    workspacePrefix: string,
    workspaceUpdate: WorkspaceUpdate,
  ): Promise<void> {
    return this.getDelegatedService().updateWorkspace(
      workspacePrefix,
      workspaceUpdate,
    )
  }

  getPresetWorkspaceSummaries(): Promise<WorkspaceSummary[]> {
    return this.getDelegatedService().getPresetWorkspaceSummaries()
  }

  cloneWorkspace(
    workspacePrefix: string,
    newName?: string,
    newDescription?: string,
  ): Promise<string | undefined> {
    return this.getDelegatedService().cloneWorkspace(
      workspacePrefix,
      newName,
      newDescription,
    )
  }

  deleteWorkspace(workspacePrefix: string): Promise<void> {
    return this.getDelegatedService().deleteWorkspace(workspacePrefix)
  }

  async getSavedViews(workspacePrefix: string): Promise<SavedView[]> {
    try {
      const savedViews = await this.getDelegatedService().getSavedViews(
        workspacePrefix,
      )
      if (!this.hasFailedOver) {
        for (const savedView of savedViews) {
          try {
            await this.localService.createSavedView(
              workspacePrefix,
              savedView.gridKey,
              savedView.savedView,
              true,
              savedView.savedViewId,
              savedView.savedViewPrefix,
            )
          } catch (e) {
            console.error(`Error syncing saved view to local storage: ${e}}`)
          }
        }
      }
      return savedViews
    } catch (e) {
      if (!this.hasFailedOver) {
        this.triggerFailover('Failed to fetch grid layouts.')
        return await this.getSavedViews(workspacePrefix)
      }
      return []
    }
  }

  async updateSavedView(
    savedViewPrefix: string,
    update: SavedViewUpdate,
  ): Promise<void> {
    try {
      return await this.getDelegatedService().updateSavedView(
        savedViewPrefix,
        update,
      )
    } catch (e) {
      console.error(
        `Failed to update saved view ${savedViewPrefix}. ${e.message}`,
      )
      return Promise.resolve()
    }
  }

  createSavedView(
    workspacePrefix: string,
    gridKey: string,
    savedView: AgGridState,
    isPersistent: boolean,
  ): Promise<string> {
    return this.getDelegatedService().createSavedView(
      workspacePrefix,
      gridKey,
      savedView,
      isPersistent,
    )
  }

  private getDelegatedService(): WorkspaceService {
    return this.hasFailedOver ? this.localService : this.httpService
  }
}
